import Brands from '@/components/brands';
import { useI18n } from '@/lib/i18n';
import { Dialog, DialogPanel } from '@headlessui/react';
import { ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Instagram from '@images/Instagram.svg';
import Logo from '@images/Logo.svg';
import TikTok from '@images/TikTok.svg';
import { useEffect, useState } from 'react';

export default function FreeTrialModal() {
  const { t, formatNumber, cheapestPrice } = useI18n();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const modalKey = 'freeTrialModalLastShown';

    const hasThirtyDaysPassed = (timestamp: string | null) => {
      if (!timestamp) return true;
      const lastShown = new Date(timestamp).getTime();
      return Date.now() - lastShown > 30 * 24 * 60 * 60 * 1000;
    };

    if (hasThirtyDaysPassed(localStorage.getItem(modalKey))) {
      setTimeout(() => {
        setIsOpen(true);
        localStorage.setItem(modalKey, new Date().toISOString());
      }, 5000);
    }
  }, []);

  return (
    <Dialog
      open={isOpen}
      onClose={() => setIsOpen(false)}
      transition
      className="fixed inset-0 z-100 flex w-screen items-center justify-center bg-yellow-900/30 p-4 transition duration-300 ease-out data-[closed]:opacity-0"
    >
      <DialogPanel className="relative max-w-lg overflow-hidden rounded-xl bg-white shadow-xl">
        <button type={'button'} className="absolute top-0 right-0 p-3 text-gray-500 hover:text-black lg:m-3" onClick={() => setIsOpen(false)}>
          <XMarkIcon className={'size-6'} />
        </button>

        <div className="space-y-3 p-6 lg:p-12">
          <div className="flex justify-center lg:pb-3">
            <img src={Logo} alt={'Boostbuddy Logo'} className="h-12 w-auto lg:h-16" />
          </div>

          <h2 className="text-2xl font-semibold tracking-tight text-balance text-black sm:text-4xl">{t('components.free_trial_modal.title')}</h2>

          <p className={'text-gray-700'}>{t('components.free_trial_modal.text')}</p>

          <div className="mt-6 flex flex-col gap-3">
            <a
              className={'flex items-center rounded-xl border border-gray-300 px-4 py-3 font-bold hover:border-gray-400'}
              href={route('instagram.trial')}
            >
              <img src={Instagram} alt={'Instagram'} className="mr-2 h-5 w-auto" />
              {t('components.free_trial_modal.Free :Service Likes', { service: 'Instagram' })}
            </a>
            <a
              className={'flex items-center rounded-xl border border-gray-300 px-4 py-3 font-bold hover:border-gray-400'}
              href={route('tiktok.trial')}
            >
              <img src={TikTok} alt={'TikTok'} className="mr-2 h-5 w-auto" />
              {t('components.free_trial_modal.Free :Service Likes', { service: 'TikTok' })}
            </a>

            <div className="mt-6 text-center text-sm text-gray-700">{t('components.free_trial_modal.Already convinced?')}</div>

            <a href={route('instagram')} className="flex items-center rounded-xl bg-orange-400/30 px-3.5 py-3 hover:opacity-75 md:text-base">
              <img src={Instagram} alt={'Instagram'} className="mr-2 h-5 w-auto" />
              <div className={'flex-1 text-left'}>
                <div className={'font-bold text-black'}>{t('components.free_trial_modal.Continue with :service', { service: 'Instagram' })}</div>
                <div className={'text-sm leading-none text-black'}>
                  {t('components.free_trial_modal.From :price', { price: formatNumber(cheapestPrice, true) })}
                </div>
              </div>
              <ChevronRightIcon className={'ml-6 size-6 text-orange-800/50'} />
            </a>

            <a href={route('tiktok')} className="flex items-center rounded-xl bg-pink-400/30 px-3.5 py-3 hover:opacity-75 md:text-base">
              <img src={TikTok} alt={'TikTok'} className="mr-2 h-5 w-auto" />
              <div className={'flex-1 text-left'}>
                <div className={'font-bold text-black'}>{t('components.free_trial_modal.Continue with :service', { service: 'TikTok' })}</div>
                <div className={'text-sm leading-none text-black'}>
                  {t('components.free_trial_modal.From :price', { price: formatNumber(cheapestPrice, true) })}
                </div>
              </div>
              <ChevronRightIcon className={'ml-6 size-6 text-pink-800/50'} />
            </a>
          </div>

          <div className="flex justify-center">
            <Brands showReviews={true} />
          </div>
        </div>
      </DialogPanel>
    </Dialog>
  );
}
